import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpUtils } from 'app/shared/http-utils';
import { FacetModel } from '../facet/facet.model';
import { catchError, map, tap } from 'rxjs';
import { Facet, FacetId } from '../facet/facet';
import { FacetsResponse } from '../facet/facets-response';
import { ErrorHandlerService } from 'app/shared/error-handler.service';
import { Chapter, ChapterPatch, ChapterPost } from './chapter';
import { FacetService } from '../facet/facet.service';
import { TextService } from 'app/shared/text.service';
import { FacetFilterService } from '../facet/facet-filter.service';
import { FacetUtils } from '../utils/facet-utils';
import { DateConst } from '../../shared/date.const';
import { TokenStorageService } from 'app/auth/token-storage.service';

@Injectable({
  providedIn: 'root',
})
export class ChapterService {
  private http = inject(HttpClient);
  private text = inject(TextService);
  private errorService = inject(ErrorHandlerService);
  private facetService = inject(FacetService);
  private facetFilter = inject(FacetFilterService);
  private tokenStorage = inject(TokenStorageService);
  private apiUrl = HttpUtils.getApiUrl('/lenses/v1/units');
  private chapterApiUrl = HttpUtils.getApiUrl('/lenses/v1/units/chapter');
  private selectedFacetSignal = signal<FacetModel | null>(null);
  private selectedFacetsSignal = signal<FacetModel[]>([]);
  // set as chapter on init, and when navigated last value is not cleared
  selectedFacetTypeId = signal<number | null>(this.tokenStorage.getFacetKey() ?? FacetId.chapter);
  ongoingChapter = signal<{ id: string }[]>([]);

  readonly selectedFacet = this.selectedFacetSignal.asReadonly();
  readonly selectedFacets = this.selectedFacetsSignal.asReadonly();

  chapters = signal<FacetModel[]>([]);
  chapter = signal<FacetModel | null>(null);
  chapterFacets = signal<FacetModel[]>([]);
  selectedFacetType = computed(() =>
    this.facetService.userFacetTypes().find((type) => type.id === this.selectedFacetTypeId()),
  );
  hasNextChapter = computed(() => {
    const currentChapterIndex = this.chapters().findIndex((chapter) => chapter.id === this.chapter()?.id);
    return !!this.chapters()[currentChapterIndex + 1];
  });

  setDefaultFacetType(id?: number) {
    if (id) {
      this.setSelectedFacetType(id);
      return;
    }
    this.setSelectedFacetType(this.tokenStorage.getFacetKey() ?? FacetId.chapter);
  }

  setSelectedFacetType(id: number | null) {
    this.selectedFacetTypeId.set(id);
    this.tokenStorage.saveFacetKey(id);
  }

  resetSelectedFacetType() {
    this.selectedFacetTypeId.set(FacetId.chapter);
    this.tokenStorage.saveFacetKey(FacetId.chapter);
  }

  setSelectedFacet(facet: FacetModel | null) {
    this.selectedFacetSignal.set(facet);
  }

  setSelectedAllFacets() {
    this.selectedFacetsSignal.set(this.chapters());
  }

  setUnselectedAllFacets() {
    this.selectedFacetsSignal.set([]);
  }

  saveChapter(chapter: ChapterPost | ChapterPatch, id: string | undefined) {
    if (id) {
      return this.updateChapter(chapter, id);
    } else {
      return this.createChapter(chapter as ChapterPost);
    }
  }

  createChapter(chapter: ChapterPost) {
    return this.http
      .post<Chapter>(this.chapterApiUrl, chapter)
      .pipe(catchError((err) => this.errorService.handle(err)));
  }

  updateChapter(chapter: ChapterPost | ChapterPatch, id: string) {
    return this.http
      .patch<Chapter>(this.chapterApiUrl + '/' + id, chapter)
      .pipe(catchError((err) => this.errorService.handle(err)));
  }

  getNextChapter() {
    return this.getAll(FacetId.chapter, true);
  }

  getChapter(id: string) {
    return this.http.get<FacetModel>(`${this.apiUrl}/${id}`).pipe(
      tap((chapter) => this.chapter.set(chapter)),
      catchError((err) => this.errorService.handle(err)),
    );
  }

  setChapter(chapter: FacetModel): void {
    this.chapter.set(chapter);
  }

  getAll(selectedFacetTypeId?: number, skipFilters?: boolean, resetChapters?: boolean) {
    this.chapters.set([]);
    const start = this.facetFilter.startDate();
    const end = this.facetFilter.endDate();
    const facetTypeId = selectedFacetTypeId ? selectedFacetTypeId : (this.selectedFacetTypeId() as number);

    let params = new HttpParams()
      .set('take', 50) // TODO infinity scroll
      .set('lenseId', facetTypeId);
    if (!skipFilters) {
      params = params.set('start', start).set('end', end);
    }

    return this.http.get<FacetsResponse<FacetModel>>(`${this.apiUrl}`, { params }).pipe(
      map((response) => response.data),
      map((chapters) => FacetUtils.hronologicalSort(chapters)),
      tap((chapters) => this.chapters.set(chapters)),
      tap(() => {
        if (!resetChapters) {
          this.selectedFacetTypeId.set(facetTypeId);
        }
      }),
      catchError((err) => this.errorService.handle(err)),
    );
  }

  getChapterFacets(startDate?: string, endDate?: string) {
    const start = startDate ? startDate : this.facetFilter.startDate();
    const end = endDate ? endDate : this.facetFilter.endDate();
    const params = new HttpParams()
      .set('take', 50) // TODO infinity scroll
      .set('excludedLenses', FacetId.chapter)
      .set('start', start)
      .set('end', end);
    return this.http.get<FacetsResponse<FacetModel>>(`${this.apiUrl}`, { params }).pipe(
      map((response) => response.data),
      map((chapters) => FacetUtils.hronologicalSort(chapters)),
      tap((chapterFacets) => this.chapterFacets.set(chapterFacets)),
      catchError((err) => this.errorService.handle(err)),
    );
  }

  deleteFacet(id: string) {
    return this.http
      .delete(`${this.apiUrl}/${id}`)
      .pipe(catchError((err) => this.errorService.handle(err, this.text.get('failed_delete_facet'))));
  }

  clearFacets() {
    this.chapterFacets.set([]);
  }

  reloadChapters() {
    const isChaptersScreen = this.facetFilter.isChaptersScreen();
    if (isChaptersScreen) {
      return this.getAll();
    } else {
      return this.getChapterFacets();
    }
  }

  getOngoingChapter(lenseId: number) {
    this.ongoingChapter.set([]);
    return this.http.get<FacetsResponse<{ id: string }>>(`${this.apiUrl}/ongoing/lense/${lenseId}`).pipe(
      map((response) => response.data),
      tap((ongoing) => this.ongoingChapter.set(ongoing)),
      catchError((err) => this.errorService.handle(err)),
    );
  }
}
